import React, { useMemo } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { BreakdownResponse, BreakdownRow } from '../../services/caseWorkflowService';
import {
    DETECTION_SIGNAL_TOOLTIP, MULTI_VALUED_CAPTION, count
} from './caseWorkflowVocabulary';
import { MAGNITUDE_HUE, NULL_BUCKET_COLOUR, byPriority, priorityColour, priorityLabel } from './caseWorkflowPalette';
import { useThemeMode } from '../../theme/ThemeModeProvider';
import { dsVar } from '../../theme/tokens';

interface BreakdownChartProps {
    data: BreakdownResponse;
    /** Bars beyond this fold into a labelled remainder rather than scrolling forever. */
    maxBars?: number;
    onSelect?: (value: string | null) => void;
    selected?: string | null;
}

const isPriority = (dimension: string) => dimension === 'priority' || dimension === 'machine_priority';

/**
 * A dimension breakdown, as horizontal bars.
 *
 * Horizontal because the category labels are words ("Corporate Current
 * Account", "account_takeover") and a vertical bar chart would either truncate
 * them or turn them on their side.
 *
 * Colour is a single hue for everything except priority. Each bar already
 * carries its own label, so a hue per bar would encode a categorical difference
 * the chart is not making — the rainbow anti-pattern. Priority is the exception
 * because it is a semantic-heat scale a reader already knows from the benchmark
 * screen, and reusing it there costs nothing and gains recognition.
 */
const BreakdownChart: React.FC<BreakdownChartProps> = ({ data, maxBars = 8, onSelect, selected }) => {
    const { resolved } = useThemeMode();
    const rows = useMemo(() => {
        const valued = data.rows.map((row) => ({
            ...row,
            value: row.dimensionValue,
            magnitude: (data.level === 'case' ? row.cases : row.transitions) ?? 0
        }));

        if (isPriority(data.dimension)) {
            return valued.sort((a, b) => byPriority(a.value || '', b.value || ''));
        }

        const sorted = valued.sort((a, b) => b.magnitude - a.magnitude);
        if (sorted.length <= maxBars) return sorted;

        // Past the cut the remainder is named and summed rather than dropped —
        // a chart whose bars do not account for its own total is not readable.
        const head = sorted.slice(0, maxBars);
        const tail = sorted.slice(maxBars);
        return [
            ...head,
            {
                dimensionValue: `${tail.length} more`,
                bucket: null,
                transitions: null,
                cases: null,
                value: null,
                magnitude: tail.reduce((sum, r) => sum + r.magnitude, 0)
            } as BreakdownRow & { value: string | null; magnitude: number }
        ];
    }, [data, maxBars]);

    const max = Math.max(...rows.map((r) => r.magnitude), 1);
    const unitWord = data.level === 'case' ? 'cases' : 'transitions';

    /**
     * An absent value is a named bucket, never a blank and never a zero.
     * account_type carries no nullBucket and is simply not written at the alert
     * stage, so its absence is labelled as structural rather than as a tier.
     */
    const labelFor = (value: string | null): string => {
        if (value === null) return data.availableOn ? 'Not applicable at this stage' : 'Not recorded';
        if (isPriority(data.dimension) && /^P[1-5]$/.test(value)) return priorityLabel(value);
        if (value === data.nullBucket) {
            return value === 'never_assigned' ? 'Never assigned'
                : value === 'unassigned' ? 'Not yet assigned'
                : value === 'undeclared' ? 'No workflow declared'
                : value === 'unrecorded' ? 'Not recorded'
                : value;
        }
        return value.replace(/_/g, ' ');
    };

    const colourFor = (value: string | null): string => {
        if (value === null || value === data.nullBucket) return NULL_BUCKET_COLOUR;
        if (isPriority(data.dimension)) return priorityColour(value, resolved);
        return MAGNITUDE_HUE;
    };

    if (!rows.length) {
        return (
            <Typography sx={{ fontSize: '0.8125rem', color: dsVar('fg-muted'), fontStyle: 'italic' }}>
                Nothing recorded for this breakdown in the selected window.
            </Typography>
        );
    }

    return (
        <Box>
            <Box className="flex flex-col gap-2">
                {rows.map((row) => {
                    const isSelected = selected !== undefined && selected === row.value;
                    const clickable = Boolean(onSelect) && row.value !== null;
                    return (
                        <Box
                            key={String(row.dimensionValue)}
                            onClick={clickable ? () => onSelect?.(row.value) : undefined}
                            className={clickable ? 'cursor-pointer group' : ''}
                            sx={{ opacity: selected && !isSelected ? 0.55 : 1, transition: 'opacity 120ms' }}
                        >
                            <Box className="flex items-baseline justify-between gap-3 mb-1">
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        color: isSelected ? dsVar('accent-fg') : dsVar('fg-soft'),
                                        textTransform: 'capitalize'
                                    }}
                                >
                                    {labelFor(row.value)}
                                </Typography>
                                {/* The value is direct-labelled, so identity never rests on colour. */}
                                <Typography sx={{ fontSize: '0.75rem', color: dsVar('fg-2'), fontVariantNumeric: 'tabular-nums' }}>
                                    {count(row.magnitude)}
                                </Typography>
                            </Box>
                            <Tooltip title={`${count(row.magnitude)} ${unitWord}`} arrow placement="top">
                                <Box sx={{ height: 8, borderRadius: '4px', backgroundColor: dsVar('page'), overflow: 'hidden' }}>
                                    <Box
                                        sx={{
                                            height: '100%',
                                            width: `${Math.max((row.magnitude / max) * 100, row.magnitude > 0 ? 2 : 0)}%`,
                                            backgroundColor: colourFor(row.value),
                                            borderRadius: '4px',
                                            transition: 'width 200ms'
                                        }}
                                    />
                                </Box>
                            </Tooltip>
                        </Box>
                    );
                })}
            </Box>

            {/*
              * The three contract flags drive this chrome, not a local opinion.
              * Each caption prevents one specific misreading of the bars above.
              */}
            <Box className="mt-3 flex flex-col gap-1">
                {data.multiValued && (
                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                        {MULTI_VALUED_CAPTION}
                    </Typography>
                )}
                {data.dimension === 'alert_type' && (
                    <Box className="flex items-center gap-1">
                        <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                            These are platform-derived signals, not your TMS scenario codes
                        </Typography>
                        <Tooltip title={DETECTION_SIGNAL_TOOLTIP} arrow placement="top">
                            <InfoOutlinedIcon sx={{ fontSize: 12, color: dsVar('fg-muted'), cursor: 'help' }} />
                        </Tooltip>
                    </Box>
                )}
                {data.availableOn && (
                    <Typography sx={{ fontSize: '0.6875rem', color: dsVar('fg-muted') }}>
                        Recorded only on {data.availableOn.join(' and ')} records — earlier stages have no value to show,
                        which is not the same as zero.
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

export default BreakdownChart;
